import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getCDNURL } from "@/shared/utils/commons";
import Image from "next/image";
import { useState } from "react";

/**
 * Feature comparison table for /page/graphy-vs-alternative.
 *
 * Four column containers sit side-by-side: "Features" | Graphy | Competitor A | Competitor B.
 * Each column is a vertical flex stack of equal-height cells so rows line up across
 * columns without a CSS grid. The Graphy column carries a single continuous
 * lavender gradient background for the whole column (rounded top & bottom) — matches
 * the Figma reference exactly. Chevrons on the Features header cycle through
 * additional competitors.
 */

type Cell = boolean | string;

type FeatureRow = {
  label: string;
  graphy: Cell;
  perCompetitor: Record<string, Cell>;
};

type Competitor = {
  key: string;
  name: string;
  logoSrc: string;
};

const BASE = `${getCDNURL()}graphy-web/V4/images/Alternative(Marketing pages)/graphy-vs-alternative/Feature Comparison`;

const competitors: Competitor[] = [
  { key: "classplus", name: "Classplus", logoSrc: `${BASE}/classplus logo.png` },
  { key: "tagmango", name: "Tagmango", logoSrc: `${BASE}/tag mango logo.png` },
  { key: "learnyst", name: "Learnyst", logoSrc: `${BASE}/Learnyst logo.png` },
  { key: "appx", name: "AppX", logoSrc: `${BASE}/Appx logo.png` },
  { key: "exily", name: "Exily", logoSrc: `${BASE}/exily logo.png` },
];

const featureRows: FeatureRow[] = [
  {
    label: "Website",
    graphy: "Full multi-page website",
    perCompetitor: {
      classplus: "Basic website pages",
      tagmango: "One-page",
      learnyst: "Up to 200 pages",
      appx: "Full multi-page website",
      exily: "Basic website pages",
    },
  },
  {
    label: "Mobile Apps",
    graphy: true,
    perCompetitor: { classplus: true, tagmango: true, learnyst: true, appx: true, exily: true },
  },
  {
    label: "White-Label",
    graphy: true,
    perCompetitor: { classplus: true, tagmango: true, learnyst: true, appx: true, exily: true },
  },
  {
    label: "Storage",
    graphy: "Unlimited",
    perCompetitor: {
      classplus: "Unlimited",
      tagmango: "Unlimited",
      learnyst: "Unlimited",
      appx: "Unlimited",
      exily: "Unlimited",
    },
  },
  {
    label: "Products",
    graphy: "Unlimited",
    perCompetitor: {
      classplus: "Unlimited",
      tagmango: "Unlimited",
      learnyst: "Up to 200",
      appx: "Unlimited",
      exily: "Unlimited",
    },
  },
  {
    label: "Landing Page Templates",
    graphy: "Unlimited",
    perCompetitor: {
      classplus: "NA",
      tagmango: "12",
      learnyst: "50",
      appx: "NA",
      exily: "NA",
    },
  },
  {
    label: "Simultaneous live sessions",
    graphy: "3",
    perCompetitor: {
      classplus: "1",
      tagmango: "1",
      learnyst: "Unlimited",
      appx: "NA",
      exily: "1",
    },
  },
  {
    label: "Admin Accounts",
    graphy: "Unlimited",
    perCompetitor: {
      classplus: "15",
      tagmango: "15",
      learnyst: "Unlimited",
      appx: "NA",
      exily: "5",
    },
  },
  {
    label: "Digital Products",
    graphy: true,
    perCompetitor: {
      classplus: true,
      tagmango: false,
      learnyst: true,
      appx: true,
      exily: true,
    },
  },
  {
    label: "Tin Can API Support",
    graphy: true,
    perCompetitor: {
      classplus: true,
      tagmango: false,
      learnyst: true,
      appx: false,
      exily: false,
    },
  },
  {
    label: "Coupon Codes",
    graphy: true,
    perCompetitor: {
      classplus: true,
      tagmango: true,
      learnyst: true,
      appx: true,
      exily: true,
    },
  },
];

/* ── Cell renderers ────────────────────────────────────────────────── */

const CheckIcon = () => (
  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-[#2F9E44] text-[#2F9E44]">
    <FontAwesomeIcon icon={faCheck} className="text-[12px]" />
  </span>
);

const XIcon = () => (
  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-[#E11D48] text-[#E11D48]">
    <FontAwesomeIcon icon={faXmark} className="text-[12px]" />
  </span>
);

const CellView = ({ value }: { value: Cell }) => {
  if (value === true) return <CheckIcon />;
  if (value === false) return <XIcon />;
  return (
    <span className="whitespace-pre-line text-[14px] leading-snug text-[#4B5563] md:text-[15px]">
      {value}
    </span>
  );
};

/* ── Column rendering primitives ───────────────────────────────────── */

// Uniform row height so columns align. Increased slightly so two-line
// text cells (e.g. "Full multi-page website") don't crowd the row.
const ROW_H = "h-[92px]";
const HEADER_H = "h-[148px]"; // space for logo + nav arrows

const FeaturesColumn = ({
  onPrev,
  onNext,
  canGoLeft,
  canGoRight,
}: {
  onPrev: () => void;
  onNext: () => void;
  canGoLeft: boolean;
  canGoRight: boolean;
}) => (
  <div className="flex flex-col">
    <div className={`flex ${HEADER_H} flex-col items-start justify-start gap-5 px-2 pt-4`}>
      <span className="text-[16px] font-semibold text-[#0C0C0C] md:text-[18px]">
        Features
      </span>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onPrev}
          disabled={!canGoLeft}
          aria-label="Previous competitor"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-black/15 text-[#0C0C0C] transition hover:bg-black/5 disabled:opacity-30"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-[12px]" />
        </button>
        <button
          type="button"
          onClick={onNext}
          disabled={!canGoRight}
          aria-label="Next competitor"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-black/15 text-[#0C0C0C] transition hover:bg-black/5 disabled:opacity-30"
        >
          <FontAwesomeIcon icon={faChevronRight} className="text-[12px]" />
        </button>
      </div>
    </div>

    {featureRows.map((row) => (
      <div
        key={row.label}
        className={`flex ${ROW_H} items-center px-2 text-[14px] leading-snug text-[#4B5563] md:text-[15px]`}
      >
        {row.label}
      </div>
    ))}
  </div>
);

const GraphyColumn = () => (
  <div
    className="flex flex-col overflow-hidden rounded-2xl"
    style={{
      background:
        "linear-gradient(180deg, #E9E5FF 0%, #F1EDFF 35%, #F7F3FF 70%, #FBF9FF 100%)",
    }}
  >
    <div className={`flex ${HEADER_H} items-center justify-center px-2`}>
      <Image
        src={`${BASE}/Graphy logo.png`}
        alt="Graphy"
        width={160}
        height={40}
        className="h-auto w-auto max-w-[160px] object-contain"
      />
    </div>

    {featureRows.map((row) => (
      <div
        key={row.label}
        className={`flex ${ROW_H} items-center justify-center px-2 text-center`}
      >
        <CellView value={row.graphy} />
      </div>
    ))}
  </div>
);

const CompetitorColumn = ({ competitor }: { competitor: Competitor }) => (
  <div className="flex flex-col">
    <div className={`flex ${HEADER_H} items-center justify-center px-2`}>
      <Image
        src={competitor.logoSrc}
        alt={competitor.name}
        width={160}
        height={40}
        className="h-auto w-auto max-w-[160px] object-contain"
      />
    </div>

    {featureRows.map((row) => (
      <div
        key={`${row.label}-${competitor.key}`}
        className={`flex ${ROW_H} items-center justify-center px-2 text-center`}
      >
        <CellView value={row.perCompetitor[competitor.key]} />
      </div>
    ))}
  </div>
);

/* ── Orchestrator ──────────────────────────────────────────────────── */

const GraphyVsComparisonSection = () => {
  const [startIdx, setStartIdx] = useState(0);
  const visibleCount = 2;
  const maxStart = Math.max(0, competitors.length - visibleCount);
  const visibleCompetitors = competitors.slice(startIdx, startIdx + visibleCount);

  const canGoLeft = startIdx > 0;
  const canGoRight = startIdx < maxStart;

  return (
    <section className="relative w-full bg-white">
      <div className="container mx-auto px-4 py-16 sm:px-6 md:px-8 md:py-24 lg:px-12">
        <div className="mx-auto max-w-6xl overflow-x-auto">
          <div
            className="grid min-w-[720px] gap-x-2 md:gap-x-4"
            style={{
              gridTemplateColumns:
                "minmax(160px, 1.1fr) minmax(180px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)",
            }}
          >
            <FeaturesColumn
              onPrev={() => canGoLeft && setStartIdx((i) => i - 1)}
              onNext={() => canGoRight && setStartIdx((i) => i + 1)}
              canGoLeft={canGoLeft}
              canGoRight={canGoRight}
            />
            <GraphyColumn />
            {visibleCompetitors.map((c) => (
              <CompetitorColumn key={c.key} competitor={c} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default GraphyVsComparisonSection;
